import { createContext, useContext, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api/client';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      const userData = await api.getMe();
      setUser(userData);
      // Clear any localStorage user ID from old system
      localStorage.removeItem('userId');
    } catch (error) {
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  const register = async (name, password) => {
    try {
      const response = await api.register(name, password);
      setUser(response.user); // Extract user from response
      // Clear any localStorage user ID
      localStorage.removeItem('userId');
      navigate('/trips');
      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Registration failed',
        statusCode: error.statusCode
      };
    }
  };

  const login = async (name, password) => {
    try {
      const response = await api.login(name, password);
      setUser(response.user); // Extract user from response
      // Clear any localStorage user ID
      localStorage.removeItem('userId');
      navigate('/trips');
      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Login failed',
        statusCode: error.statusCode
      };
    }
  };

  const logout = async () => {
    try {
      await api.logout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      setUser(null);
      navigate('/login');
    }
  };

  const claimAccount = async (userId, password) => {
    try {
      const response = await api.claimAccount(userId, password);
      setUser(response.user); // Extract user from response
      // Clear any localStorage user ID
      localStorage.removeItem('userId');
      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Failed to claim account',
        statusCode: error.statusCode
      };
    }
  };

  const value = {
    user,
    loading,
    register,
    login,
    logout,
    claimAccount,
    checkAuth,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
